import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { SendReportButton } from '../common/SendReportButton';
import { Download, CreditCard, Mail, Users, Plane, Search, AlertOctagon, Pencil, CalendarRange, Plus, BadgeCheck, RefreshCw, GitCompareArrows, FileText, Check, X, History, ShieldCheck, Trash2 } from 'lucide-react';
import { DirhamIcon } from '../icons/DirhamIcon';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TABS = [
  { key: 'bank', label: 'Bank Details', icon: CreditCard,
    activeCls: 'bg-sky-500 text-white border-sky-500 shadow-md',
    inactiveCls: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' },
  { key: 'contact', label: 'Contact Info', icon: Mail,
    activeCls: 'bg-emerald-500 text-white border-emerald-500 shadow-md',
    inactiveCls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
  { key: 'dependents', label: 'Dependents', icon: Users,
    activeCls: 'bg-rose-500 text-white border-rose-500 shadow-md',
    inactiveCls: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100' },
  { key: 'staff-cost', label: 'Staff Cost', icon: DirhamIcon,
    activeCls: 'bg-amber-500 text-white border-amber-500 shadow-md',
    inactiveCls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
  { key: 'salary', label: 'Contract Salary', icon: FileText,
    activeCls: 'bg-teal-500 text-white border-teal-500 shadow-md',
    inactiveCls: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100' },
  { key: 'visa', label: 'Visa', icon: Plane,
    activeCls: 'bg-violet-500 text-white border-violet-500 shadow-md',
    inactiveCls: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100' },
  { key: 'lop', label: 'LOP', icon: AlertOctagon,
    activeCls: 'bg-orange-500 text-white border-orange-500 shadow-md',
    inactiveCls: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
  { key: 'esc', label: 'ESC Accreditation', icon: BadgeCheck,
    activeCls: 'bg-red-500 text-white border-red-500 shadow-md',
    inactiveCls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
  { key: 'iloe', label: 'ILOE', icon: ShieldCheck,
    activeCls: 'bg-cyan-600 text-white border-cyan-600 shadow-md',
    inactiveCls: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100' },
  { key: 'staff-history', label: 'Staff History', icon: History,
    activeCls: 'bg-indigo-500 text-white border-indigo-500 shadow-md',
    inactiveCls: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' },
];

const formatNum = (n) => (typeof n === 'number' ? n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '0');
const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const StaffDirectory = () => {
  const [activeTab, setActiveTab] = useState('bank');
  const [includeDependents, setIncludeDependents] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lopYearFilter, setLopYearFilter] = useState('all');
  const [historyYear, setHistoryYear] = useState(null);
  const [historyYears, setHistoryYears] = useState([]);
  const [editingPreRecord, setEditingPreRecord] = useState(null); // { employee_id, name, current }
  const [preRecordValue, setPreRecordValue] = useState('');
  const [preRecordNote, setPreRecordNote] = useState('');
  const [savingPreRecord, setSavingPreRecord] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  const [lopSettings, setLopSettings] = useState({ effective_from_year: null, effective_from_month: null });
  const [showLopSettings, setShowLopSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ year: '', month: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  // Manual LOP entry
  const [addingEntryFor, setAddingEntryFor] = useState(null); // {employee_id, name, employee_code}
  const [entryForm, setEntryForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, days: '', reason: '' });
  const [savingEntry, setSavingEntry] = useState(false);

  // LOP recompute + legacy-vs-current comparison
  const [recomputing, setRecomputing] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareRows, setCompareRows] = useState([]);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [changedOnly, setChangedOnly] = useState(true);

  const restoreLegacyPreRecord = async (row) => {
    const leaveDerived = row.leave_derived ?? row.current;
    const willBe = Math.max(0, (row.legacy ?? 0)); // legacy total restored (= leave_derived + pre_record)
    if (!window.confirm(`Restore ${row.name}'s historical LOP?\n\nThis re-applies the unexplained portion of the imported value (${formatNum(row.legacy)}) as pre-record, setting LOP to ≈ ${formatNum(Math.max(willBe, leaveDerived))} (leave-driven ${formatNum(leaveDerived)} + historical). Reversible.`)) return;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/api/staff-directory/lop/${row.employee_id}/restore-legacy-prerecord`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Historical LOP restored', description: `${row.name}: LOP now ${formatNum(r.data.lop_days)} (pre-record ${formatNum(r.data.pre_record)}).` });
      reloadActive();
      openCompare();
    } catch (e) {
      toast({ title: 'Restore failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const openCompare = async () => {
    setShowCompare(true);
    setLoadingCompare(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/staff-directory/lop/comparison`, { headers: { Authorization: `Bearer ${token}` } });
      setCompareRows(r.data?.rows || []);
    } catch (e) {
      setCompareRows([]);
      toast({ title: 'Could not load comparison', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setLoadingCompare(false); }
  };

  const recomputeLop = async () => {
    if (!window.confirm("Recompute every employee's LOP from their leave records?\n\nImported values are backed up (reversible). This updates the stored LOP used by EOSB/leave-salary.")) return;
    setRecomputing(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/api/staff-directory/lop/recompute-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'LOP recomputed', description: `${r.data.changed} of ${r.data.total_employees} employees updated.` });
      reloadActive();
      openCompare();
    } catch (e) {
      toast({ title: 'Recompute failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setRecomputing(false); }
  };

  // ESC Accreditation snapshot history
  const [escHistory, setEscHistory] = useState([]);
  const [showEscHistory, setShowEscHistory] = useState(false);
  const reloadEscHistory = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/staff-directory/esc/history/list?limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setEscHistory(r.data?.snapshots || []))
      .catch(() => setEscHistory([]));
  };
  useEffect(() => {
    if (activeTab === 'esc') reloadEscHistory();
  }, [activeTab]);

  const deleteEscHistoryItem = async (id) => {
    if (!window.confirm('Delete this ESC export snapshot?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/staff-directory/esc/history/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Snapshot deleted' });
      reloadEscHistory();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const reloadActive = () => {
    axios.get(`${API}/api/staff-directory/${activeTab}`)
      .then(r => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  };

  useEffect(() => {
    if (activeTab === 'staff-history') { setSearch(''); return; }
    setLoading(true);
    setSearch('');
    axios.get(`${API}/api/staff-directory/${activeTab}`)
      .then(r => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeTab]);

  // Staff History — year options + roster for the selected year
  useEffect(() => {
    if (activeTab !== 'staff-history') return;
    setLoading(true);
    const token = localStorage.getItem('token');
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };
    const url = historyYear
      ? `${API}/api/staff-directory/staff-history?year=${historyYear}`
      : `${API}/api/staff-directory/staff-history`;
    axios.get(url, hdrs)
      .then(r => {
        setRows(r.data?.rows || []);
        if (r.data?.years) setHistoryYears(r.data.years);
        if (historyYear == null && r.data?.year) setHistoryYear(r.data.year);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeTab, historyYear]);

  useEffect(() => {
    if (activeTab !== 'lop') return;
    axios.get(`${API}/api/staff-directory/lop/settings`)
      .then(r => setLopSettings(r.data || {}))
      .catch(() => {});
  }, [activeTab]);

  const openLopSettings = () => {
    setSettingsForm({
      year: lopSettings.effective_from_year || new Date().getFullYear(),
      month: lopSettings.effective_from_month || (new Date().getMonth() + 1),
    });
    setShowLopSettings(true);
  };

  const saveLopSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/staff-directory/lop/settings`,
        { effective_from_year: parseInt(settingsForm.year), effective_from_month: parseInt(settingsForm.month) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'LOP cutoff updated' });
      setShowLopSettings(false);
      const r = await axios.get(`${API}/api/staff-directory/lop/settings`);
      setLopSettings(r.data || {});
      reloadActive();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSavingSettings(false); }
  };

  const clearLopSettings = async () => {
    if (!window.confirm('Clear the LOP cutoff and show all historical years again?')) return;
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/staff-directory/lop/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setLopSettings({ effective_from_year: null, effective_from_month: null });
      setShowLopSettings(false);
      reloadActive();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSavingSettings(false); }
  };

  const openAddEntry = (row) => {
    setAddingEntryFor({
      employee_id: row.employee_id,
      employee_code: row.employee_code,
      name: row.name,
    });
    setEntryForm({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      days: '',
      reason: '',
    });
  };

  const saveEntry = async () => {
    if (!addingEntryFor) return;
    const days = parseFloat(entryForm.days);
    if (!days || days <= 0) { toast({ title: 'Enter days > 0', variant: 'destructive' }); return; }
    setSavingEntry(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/staff-directory/lop/entries`,
        {
          employee_id: addingEntryFor.employee_id,
          year: parseInt(entryForm.year),
          month: parseInt(entryForm.month),
          days,
          reason: entryForm.reason || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'LOP entry added', description: `${days} day(s) recorded for ${MONTH_NAMES[entryForm.month - 1]} ${entryForm.year}` });
      setAddingEntryFor(null);
      reloadActive();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSavingEntry(false); }
  };

  const openPreRecordEdit = (row) => {
    setEditingPreRecord({
      employee_id: row.employee_id,
      employee_code: row.employee_code,
      name: row.name,
      current: row.historical || 0,
    });
    setPreRecordValue(String(row.historical || 0));
    setPreRecordNote('');
  };

  const savePreRecord = async () => {
    if (!editingPreRecord) return;
    const val = parseFloat(preRecordValue);
    if (isNaN(val) || val < 0) {
      toast({ title: 'Enter a valid number ≥ 0', variant: 'destructive' });
      return;
    }
    setSavingPreRecord(true);
    try {
      const token = localStorage.getItem('token');
      // Need employee_id; lop rows include it now
      await axios.put(
        `${API}/api/staff-directory/lop/${editingPreRecord.employee_id}/pre-record`,
        { pre_record_days: val, note: preRecordNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Pre-record updated' });
      setEditingPreRecord(null);
      reloadActive();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSavingPreRecord(false); }
  };

  const saveSalary = async (employeeId, value) => {
    const num = value === '' || value == null ? null : parseFloat(value);
    if (num != null && (isNaN(num) || num < 0)) {
      toast({ title: 'Enter a valid amount ≥ 0', variant: 'destructive' });
      return false;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/staff-directory/salary/${employeeId}`,
        { labour_contract_salary: num },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(prev => prev.map(r => r.employee_id === employeeId || r.id === employeeId
        ? { ...r, labour_contract_salary: num || 0 } : r));
      toast({ title: 'Contract salary updated' });
      return true;
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
      return false;
    }
  };

  const saveIloe = async (employeeId, certNo, expiry) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API}/api/staff-directory/iloe/${employeeId}`,
        { iloe_certificate_no: certNo, iloe_expiry_date: expiry },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(prev => prev.map(r => (r.employee_id === employeeId || r.id === employeeId)
        ? { ...r, iloe_certificate_no: data.iloe_certificate_no || '', iloe_expiry_date: data.iloe_expiry_date || '' } : r));
      toast({ title: 'ILOE certificate updated' });
      return true;
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
      return false;
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => Object.values(r).some(v =>
      typeof v === 'string' && v.toLowerCase().includes(q)
    ));
  }, [rows, search]);

  const download = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const depParam = (activeTab === 'staff-cost' && includeDependents) ? '&include_dependents=true' : '';
      const url = activeTab === 'staff-history'
        ? `${API}/api/staff-directory/staff-history/download?format=${format}&year=${historyYear || ''}`
        : `${API}/api/staff-directory/${activeTab}/download?format=${format}${depParam}`;
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const ysuffix = activeTab === 'staff-history' && historyYear ? `_${historyYear}` : '';
      a.download = `Staff_${activeTab.replace('-', '_')}${ysuffix}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast({ title: `Downloaded ${activeTab.replace('-', ' ')} ${format.toUpperCase()}` });
      // Refresh ESC history view if we just exported ESC
      if (activeTab === 'esc') reloadEscHistory();
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="staff-directory">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-3" data-testid="directory-subtabs">
        {TABS.map(t => {
          const I = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition border ${
                active ? t.activeCls : t.inactiveCls
              }`}
              data-testid={`directory-tab-${t.key}`}
            >
              <I className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by name, code, dept…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="directory-search"
          />
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'lop' && (
            <button
              type="button"
              onClick={openLopSettings}
              disabled={!isSuperAdmin}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border ${
                lopSettings.effective_from_year
                  ? 'bg-orange-50 border-orange-300 text-orange-700'
                  : 'bg-gray-50 border-gray-200 text-gray-600'
              } ${isSuperAdmin ? 'hover:bg-orange-100 cursor-pointer' : 'opacity-70 cursor-not-allowed'}`}
              data-testid="lop-cutoff-btn"
              title={isSuperAdmin ? 'Set LOP tracking cutoff' : 'Super admin only'}
            >
              <CalendarRange className="h-3.5 w-3.5" />
              {lopSettings.effective_from_year
                ? `Tracking from ${MONTH_NAMES[lopSettings.effective_from_month - 1]} ${lopSettings.effective_from_year}`
                : 'Set LOP cutoff'}
            </button>
          )}
          {activeTab === 'lop' && rows.length > 0 && (
            <select
              value={lopYearFilter}
              onChange={(e) => setLopYearFilter(e.target.value)}
              className="px-2 py-1.5 text-xs rounded-md border bg-white focus:outline-none focus:ring-2 focus:ring-orange-200"
              data-testid="lop-year-filter"
            >
              <option value="all">All Years</option>
              {(rows[0]?.years || []).slice().reverse().map(y => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          )}
          {activeTab === 'staff-history' && historyYears.length > 0 && (
            <select
              value={historyYear || ''}
              onChange={(e) => setHistoryYear(parseInt(e.target.value))}
              className="px-2.5 py-1.5 text-xs rounded-md border bg-white font-semibold text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              data-testid="staff-history-year-filter"
            >
              {historyYears.map(y => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          )}
          <span className="text-xs text-gray-500">{filtered.length} record(s)</span>
          {activeTab === 'staff-cost' && (
            <Button
              variant={includeDependents ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIncludeDependents(v => !v)}
              className={includeDependents ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              data-testid="staff-cost-include-dependents-toggle"
              title="Add each employee's sponsored dependent costs (insurance, air ticket, etc.) to the monthly total"
            >
              <Users className="h-4 w-4 mr-1" /> {includeDependents ? 'Dependents: ON' : 'Include Dependent Cost'}
            </Button>
          )}
          {activeTab === 'esc' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEscHistory(s => !s)}
              data-testid="esc-history-toggle"
              className="border-red-200 text-red-700 hover:bg-red-50"
            >
              <CalendarRange className="h-4 w-4 mr-1" /> History ({escHistory.length})
            </Button>
          )}
          {activeTab === 'lop' && isSuperAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={recomputeLop}
                disabled={recomputing}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="lop-recompute-btn"
                title="Recompute every employee's LOP from leave records"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${recomputing ? 'animate-spin' : ''}`} /> {recomputing ? 'Recomputing…' : 'Recompute LOP'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openCompare}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="lop-compare-btn"
                title="Compare imported vs current LOP"
              >
                <GitCompareArrows className="h-4 w-4 mr-1" /> Compare
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => download('pdf')} data-testid="directory-download-pdf">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => download('excel')} data-testid="directory-download-excel">
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
          <SendReportButton
            reportUrl={activeTab === 'staff-history'
              ? `/api/staff-directory/staff-history/download?format=pdf&year=${historyYear || ''}`
              : `/api/staff-directory/${activeTab}/download?format=pdf${(activeTab === 'staff-cost' && includeDependents) ? '&include_dependents=true' : ''}`}
            filename={`Staff_${activeTab.replace('-', '_')}${activeTab === 'staff-history' && historyYear ? '_' + historyYear : ''}.pdf`}
            defaultSubject={`Staff Report — ${TABS.find(t => t.key === activeTab)?.label || activeTab}`}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            testid="directory-email"
          />
        </div>
      </div>

      {/* ESC History panel */}
      {activeTab === 'esc' && showEscHistory && (
        <Card data-testid="esc-history-panel" className="border-red-200">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-red-700 flex items-center gap-2">
                <BadgeCheck className="h-4 w-4" /> ESC Export Snapshot History
              </h3>
              <span className="text-[11px] text-gray-500">Last {escHistory.length} export(s)</span>
            </div>
            {escHistory.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-3">No exports yet. Click PDF or Excel above to record a snapshot.</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-red-50 text-[10px] uppercase text-red-700">
                    <tr>
                      <th className="text-left p-2">Exported At</th>
                      <th className="text-left p-2">By</th>
                      <th className="text-center p-2">Format</th>
                      <th className="text-right p-2">Employees</th>
                      <th className="text-left p-2">First 5 codes</th>
                      <th className="text-right p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {escHistory.map(s => (
                      <tr key={s.id} className="border-t" data-testid={`esc-history-row-${s.id}`}>
                        <td className="p-2 font-mono">
                          {(() => { try { return new Date(s.exported_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', ''); } catch { return s.exported_at; } })()}
                        </td>
                        <td className="p-2">{s.exported_by || '—'}</td>
                        <td className="p-2 text-center uppercase font-semibold text-red-600">{s.format || 'pdf'}</td>
                        <td className="p-2 text-right font-mono">{s.employee_count}</td>
                        <td className="p-2 text-gray-500">{(s.employee_codes || []).slice(0, 5).join(', ') || '—'}{(s.employee_codes || []).length > 5 ? '…' : ''}</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost" className="text-rose-600 h-6 w-6 p-0" onClick={() => deleteEscHistoryItem(s.id)} title="Delete snapshot" data-testid={`esc-history-delete-${s.id}`}>
                            ×
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-gray-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-gray-500">No records</div>
          ) : (
            <DirectoryTable type={activeTab} rows={filtered} lopYearFilter={lopYearFilter} isSuperAdmin={isSuperAdmin} includeDependents={includeDependents} onEditPreRecord={openPreRecordEdit} onAddEntry={openAddEntry} onSaveSalary={saveSalary} onSaveIloe={saveIloe} onDataChanged={reloadActive} />
          )}
        </CardContent>
      </Card>

      {/* Edit Pre-record dialog */}
      <Dialog open={!!editingPreRecord} onOpenChange={(o) => { if (!o) setEditingPreRecord(null); }}>
        <DialogContent data-testid="prerecord-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Pre-record LOP — {editingPreRecord?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Pre-record represents legacy/migrated LOP days that aren't reflected in individual leave records.
              Setting this value adjusts the employee's stored cumulative LOP accordingly.
              Per-year totals from leave records remain untouched.
            </p>
            <div>
              <Label>Pre-record days</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={preRecordValue}
                onChange={e => setPreRecordValue(e.target.value)}
                data-testid="prerecord-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">Current: {editingPreRecord?.current ?? 0}</p>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input
                value={preRecordNote}
                onChange={e => setPreRecordNote(e.target.value)}
                placeholder="Reason for adjustment (audit log)"
                data-testid="prerecord-note"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingPreRecord(null)}>Cancel</Button>
              <Button
                onClick={savePreRecord}
                disabled={savingPreRecord}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="prerecord-save"
              >
                {savingPreRecord ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Add LOP entry dialog */}
      <Dialog open={!!addingEntryFor} onOpenChange={(o) => { if (!o) setAddingEntryFor(null); }}>
        <DialogContent data-testid="add-lop-entry-dialog">
          <DialogHeader>
            <DialogTitle>Add LOP Entry — {addingEntryFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Record an LOP day count for a specific month/year. The entry is saved as an unpaid leave record
              and appears in that year's column automatically.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Month</Label>
                <select
                  value={entryForm.month}
                  onChange={e => setEntryForm(f => ({ ...f, month: e.target.value }))}
                  className="w-full px-2 py-1.5 rounded-md border text-sm"
                  data-testid="entry-month"
                >
                  {MONTH_NAMES.map((m, i) => (<option key={i + 1} value={i + 1}>{m}</option>))}
                </select>
              </div>
              <div>
                <Label>Year</Label>
                <select
                  value={entryForm.year}
                  onChange={e => setEntryForm(f => ({ ...f, year: e.target.value }))}
                  className="w-full px-2 py-1.5 rounded-md border text-sm"
                  data-testid="entry-year"
                >
                  {Array.from({ length: 20 }).map((_, i) => {
                    const y = new Date().getFullYear() - 15 + i;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
              </div>
            </div>
            <div>
              <Label>Days</Label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={entryForm.days}
                onChange={e => setEntryForm(f => ({ ...f, days: e.target.value }))}
                placeholder="e.g. 2"
                data-testid="entry-days"
              />
            </div>
            <div>
              <Label>Reason / Note (optional)</Label>
              <Input
                value={entryForm.reason}
                onChange={e => setEntryForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Unauthorised absence — Oct 2024"
                data-testid="entry-reason"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAddingEntryFor(null)}>Cancel</Button>
              <Button onClick={saveEntry} disabled={savingEntry} className="bg-orange-600 hover:bg-orange-700" data-testid="entry-save">
                {savingEntry ? 'Saving…' : 'Add LOP'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* LOP legacy-vs-current comparison dialog */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-3xl" data-testid="lop-compare-dialog">
          <DialogHeader>
            <DialogTitle>LOP — Imported vs Current</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                "Imported" is the original value backed up before the leave-driven recompute. "Current" is derived from leave records (+ any pre-record).
              </p>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                <input type="checkbox" checked={changedOnly} onChange={e => setChangedOnly(e.target.checked)} data-testid="lop-compare-changed-only" />
                Changed only
              </label>
            </div>
            {loadingCompare ? (
              <div className="py-10 text-center text-gray-500">Loading…</div>
            ) : (
              (() => {
                const visible = compareRows.filter(r => !changedOnly || Math.abs(r.diff) > 0.01);
                if (visible.length === 0) return <div className="py-10 text-center text-gray-400 text-sm">No {changedOnly ? 'changed ' : ''}records.</div>;
                return (
                  <div className="max-h-[60vh] overflow-auto border rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-50 text-orange-800 text-xs uppercase sticky top-0">
                        <tr>
                          <th className="text-left p-2">Code</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-right p-2">Imported</th>
                          <th className="text-right p-2">Current</th>
                          <th className="text-right p-2">Δ</th>
                          <th className="text-right p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((r, i) => {
                          const changed = Math.abs(r.diff) > 0.01;
                          // Offer restore when the imported value was higher than the
                          // leave-driven amount (i.e. likely contained historical LOP).
                          const canRestore = r.legacy !== null && (r.legacy - (r.leave_derived ?? r.current)) > 0.01 && (r.pre_record ?? 0) < 0.01;
                          return (
                            <tr key={i} className={`border-t ${changed ? 'bg-orange-50/60' : ''}`} data-testid={`lop-compare-row-${r.employee_code}`}>
                              <td className="p-2 font-mono text-xs">{r.employee_code}</td>
                              <td className="p-2">{r.name}</td>
                              <td className="p-2 text-right font-mono text-xs text-gray-500">{r.legacy === null ? '—' : formatNum(r.legacy)}</td>
                              <td className="p-2 text-right font-mono text-xs font-semibold text-orange-700">{formatNum(r.current)}</td>
                              <td className={`p-2 text-right font-mono text-xs font-bold ${r.diff < 0 ? 'text-emerald-600' : r.diff > 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                                {changed ? (r.diff > 0 ? `+${formatNum(r.diff)}` : formatNum(r.diff)) : '—'}
                              </td>
                              <td className="p-2 text-right">
                                {canRestore && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-indigo-600 hover:bg-indigo-50"
                                    onClick={() => restoreLegacyPreRecord(r)}
                                    data-testid={`lop-restore-${r.employee_code}`}
                                    title="Re-apply the imported value's historical portion as pre-record"
                                  >
                                    Restore
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
            <p className="text-[11px] text-gray-400">
              {compareRows.filter(r => Math.abs(r.diff) > 0.01).length} of {compareRows.length} employees changed. Imported values remain backed up and are reversible.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* LOP cutoff settings dialog */}
      <Dialog open={showLopSettings} onOpenChange={setShowLopSettings}>
        <DialogContent data-testid="lop-settings-dialog">
          <DialogHeader>
            <DialogTitle>LOP Tracking Cutoff</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Set the month/year from which LOP records are accurate.
              Year columns will only show data from this date onwards. Older LOP days will roll into the <strong>Pre-record</strong> column for each employee.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Month</Label>
                <select
                  value={settingsForm.month}
                  onChange={e => setSettingsForm(f => ({ ...f, month: e.target.value }))}
                  className="w-full px-2 py-1.5 rounded-md border text-sm"
                  data-testid="settings-month"
                >
                  {MONTH_NAMES.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Year</Label>
                <select
                  value={settingsForm.year}
                  onChange={e => setSettingsForm(f => ({ ...f, year: e.target.value }))}
                  className="w-full px-2 py-1.5 rounded-md border text-sm"
                  data-testid="settings-year"
                >
                  {Array.from({ length: 15 }).map((_, i) => {
                    const y = new Date().getFullYear() - 10 + i;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
              </div>
            </div>
            <div className="flex justify-between items-center pt-2">
              {lopSettings.effective_from_year ? (
                <Button variant="ghost" onClick={clearLopSettings} disabled={savingSettings} className="text-rose-600" data-testid="settings-clear">
                  Clear cutoff
                </Button>
              ) : <div />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowLopSettings(false)}>Cancel</Button>
                <Button onClick={saveLopSettings} disabled={savingSettings} className="bg-orange-600 hover:bg-orange-700" data-testid="settings-save">
                  {savingSettings ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SalaryRow = ({ r, idx, isEditing, onStartEdit, onCancel, onCommit }) => {
  const [value, setValue] = useState(String(r.labour_contract_salary || ''));
  const [saving, setSaving] = useState(false);

  // Re-seed the input whenever this row enters edit mode (incl. keyboard jumps).
  useEffect(() => {
    if (isEditing) setValue(r.labour_contract_salary ? String(r.labour_contract_salary) : '');
  }, [isEditing, r.labour_contract_salary]);

  const commit = async (direction) => {
    setSaving(true);
    await onCommit(idx, r.employee_id, value, direction);
    setSaving(false);
  };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowDown') {
      e.preventDefault();
      commit('next');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      commit('prev');
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <tr className="border-t hover:bg-teal-50/40" data-testid={`salary-row-${r.employee_code}`}>
      <td className="p-3 text-gray-500">{idx + 1}</td>
      <td className="p-3 font-mono text-xs">{r.employee_code}</td>
      <td className="p-3 font-semibold">{r.name}</td>
      <td className="p-3 text-xs text-gray-600">{r.department}</td>
      <td className="p-3 text-xs text-gray-600">{r.position || '—'}</td>
      <td className="p-3 text-right bg-teal-50/50">
        {isEditing ? (
          <span className="inline-flex items-center gap-1 justify-end">
            <Input
              type="number"
              min="0"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKey}
              onFocus={(e) => e.target.select()}
              className="h-8 w-28 text-right font-mono"
              data-testid={`salary-input-${r.employee_code}`}
            />
            <button onClick={() => commit('close')} disabled={saving} className="text-emerald-600 hover:text-emerald-800 p-1" title="Save" data-testid={`salary-save-${r.employee_code}`}>
              <Check className="h-4 w-4" />
            </button>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-1" title="Cancel">
              <X className="h-4 w-4" />
            </button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 justify-end group">
            <span className={`font-mono ${r.labour_contract_salary > 0 ? 'font-bold text-teal-700' : 'text-gray-400'}`}>
              {r.labour_contract_salary > 0 ? formatNum(r.labour_contract_salary) : '—'}
            </span>
            <button onClick={() => onStartEdit(idx)} className="opacity-30 group-hover:opacity-100 transition" title="Edit contract salary" data-testid={`salary-edit-${r.employee_code}`}>
              <Pencil className="h-3.5 w-3.5 text-gray-500 hover:text-teal-600" />
            </button>
          </span>
        )}
      </td>
    </tr>
  );
};

const SalaryTable = ({ rows, onSaveSalary }) => {
  const [editingIdx, setEditingIdx] = useState(-1);
  const total = rows.reduce((acc, r) => acc + (r.labour_contract_salary || 0), 0);

  // Save the row, then (for keyboard nav) jump straight into the next/prev row's
  // input so HR can blaze down the whole list without touching the mouse.
  const handleCommit = async (idx, employeeId, value, direction) => {
    const ok = await onSaveSalary(employeeId, value);
    if (!ok) return; // keep editing this row on failure
    if (direction === 'next' && idx + 1 < rows.length) setEditingIdx(idx + 1);
    else if (direction === 'prev' && idx - 1 >= 0) setEditingIdx(idx - 1);
    else setEditingIdx(-1);
  };

  return (
    <table className="w-full text-sm">
      <thead className="bg-teal-50 text-teal-800 text-xs uppercase tracking-wider">
        <tr>
          <th className="text-left p-3">#</th>
          <th className="text-left p-3">Code</th>
          <th className="text-left p-3">Name</th>
          <th className="text-left p-3">Department</th>
          <th className="text-left p-3">Position</th>
          <th className="text-right p-3 bg-teal-100">Labour Contract Salary (AED)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <SalaryRow
            key={i}
            r={r}
            idx={i}
            isEditing={editingIdx === i}
            onStartEdit={setEditingIdx}
            onCancel={() => setEditingIdx(-1)}
            onCommit={handleCommit}
          />
        ))}
        <tr className="bg-teal-100 font-bold">
          <td className="p-3" colSpan={5}>Total</td>
          <td className="p-3 text-right font-mono text-teal-800">{formatNum(total)}</td>
        </tr>
      </tbody>
    </table>
  );
};

const ILOERow = ({ r, isEditing, onStartEdit, onCancel, onSave }) => {
  const [cert, setCert] = useState(r.iloe_certificate_no || '');
  const [expiry, setExpiry] = useState(r.iloe_expiry_date ? String(r.iloe_expiry_date).split('T')[0] : '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setCert(r.iloe_certificate_no || '');
      setExpiry(r.iloe_expiry_date ? String(r.iloe_expiry_date).split('T')[0] : '');
    }
  }, [isEditing, r.iloe_certificate_no, r.iloe_expiry_date]);

  const days = r.days_to_expiry;
  let badge = <span className="text-gray-400 text-xs">—</span>;
  if (typeof days === 'number') {
    if (days < 0) badge = <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">Expired {Math.abs(days)}d ago</span>;
    else if (days <= 30) badge = <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 font-medium">{days}d left</span>;
    else badge = <span className="px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700">{days}d left</span>;
  }

  const commit = async () => {
    setSaving(true);
    const ok = await onSave(r.employee_id || r.id, cert.trim(), expiry || null);
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <tr className="border-t hover:bg-cyan-50/40 group" data-testid={`iloe-row-${r.employee_code}`}>
      <td className="p-3 text-gray-500">{r._idx}</td>
      <td className="p-3 font-mono text-xs">{r.employee_code}</td>
      <td className="p-3 font-medium">{r.name}</td>
      <td className="p-3 text-gray-600">{r.department}</td>
      {isEditing ? (
        <>
          <td className="p-3">
            <Input value={cert} onChange={(e) => setCert(e.target.value)} placeholder="ILOE cert no." className="h-8 w-44" data-testid={`iloe-cert-input-${r.employee_code}`} />
          </td>
          <td className="p-3">
            <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-8 w-40" data-testid={`iloe-expiry-input-${r.employee_code}`} />
          </td>
          <td className="p-3 text-center">
            <div className="flex items-center gap-1 justify-center">
              <button onClick={commit} disabled={saving} className="text-emerald-600 hover:text-emerald-800 p-1" title="Save" data-testid={`iloe-save-${r.employee_code}`}><Check className="h-4 w-4" /></button>
              <button onClick={onCancel} disabled={saving} className="text-gray-400 hover:text-gray-600 p-1" title="Cancel"><X className="h-4 w-4" /></button>
            </div>
          </td>
        </>
      ) : (
        <>
          <td className="p-3 font-mono text-xs">{r.iloe_certificate_no || <span className="text-gray-300">—</span>}</td>
          <td className="p-3">{r.iloe_expiry_date ? formatDate(r.iloe_expiry_date) : <span className="text-gray-300">—</span>}</td>
          <td className="p-3 text-center">
            <div className="flex items-center gap-2 justify-center">
              {badge}
              <button onClick={onStartEdit} className="opacity-30 group-hover:opacity-100 transition" title="Edit ILOE" data-testid={`iloe-edit-${r.employee_code}`}><Pencil className="h-3.5 w-3.5 text-cyan-700" /></button>
            </div>
          </td>
        </>
      )}
    </tr>
  );
};

const ILOETable = ({ rows, onSaveIloe }) => {
  const [editingIdx, setEditingIdx] = useState(-1);
  return (
    <table className="w-full text-sm">
      <thead className="bg-cyan-50 text-cyan-800 text-xs uppercase tracking-wider">
        <tr>
          <th className="text-left p-3">#</th>
          <th className="text-left p-3">Code</th>
          <th className="text-left p-3">Name</th>
          <th className="text-left p-3">Department</th>
          <th className="text-left p-3">ILOE Certificate No</th>
          <th className="text-left p-3">Expiry</th>
          <th className="text-center p-3 bg-cyan-100">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <ILOERow
            key={r.employee_id || i}
            r={{ ...r, _idx: i + 1 }}
            isEditing={editingIdx === i}
            onStartEdit={() => setEditingIdx(i)}
            onCancel={() => setEditingIdx(-1)}
            onSave={onSaveIloe}
          />
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={7} className="p-6 text-center text-gray-400">No active staff found.</td></tr>
        )}
      </tbody>
    </table>
  );
};

const DirectoryTable = ({ type, rows, lopYearFilter = 'all', isSuperAdmin = false, includeDependents = false, onEditPreRecord, onAddEntry, onSaveSalary, onSaveIloe, onDataChanged }) => {
  const [lopDetail, setLopDetail] = useState(null); // { emp, year, month, leaves, loading }
  const [deletingId, setDeletingId] = useState(null);
  const deleteLopEntry = async (lv) => {
    if (!window.confirm(`Remove this manual LOP entry (${formatNum(lv.lop_days)} day(s))? This cannot be undone.`)) return;
    setDeletingId(lv.id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/staff-directory/lop/entries/${lv.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'LOP entry removed', description: `${formatNum(lv.lop_days)} day(s) removed.` });
      setLopDetail(prev => prev ? { ...prev, leaves: (prev.leaves || []).filter(x => x.id !== lv.id) } : prev);
      onDataChanged && onDataChanged();
    } catch (e) {
      toast({ title: 'Failed to remove entry', description: e?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };
  const openLopDetail = async (r, year, month) => {
    setLopDetail({ emp: r, year, month, leaves: [], loading: true });
    try {
      const token = localStorage.getItem('token');
      const params = { year };
      if (month) params.month = month;
      const res = await axios.get(`${API}/api/staff-directory/lop/${r.employee_id}/leaves`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setLopDetail({ emp: r, year, month, leaves: res.data?.leaves || [], loading: false });
    } catch {
      toast({ title: 'Failed to load leave records', variant: 'destructive' });
      setLopDetail(null);
    }
  };
  if (type === 'staff-history') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-indigo-50 text-indigo-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Code</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Department</th>
            <th className="text-left p-3">Designation</th>
            <th className="text-left p-3">Visa Sponsor</th>
            <th className="text-left p-3">Start Date</th>
            <th className="text-left p-3">End Date</th>
            <th className="text-right p-3">Monthly Salary</th>
            <th className="text-left p-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t hover:bg-indigo-50/40" data-testid={`staff-history-row-${r.employee_code}`}>
              <td className="p-3 text-gray-500">{i + 1}</td>
              <td className="p-3 font-mono text-xs">{r.employee_code}</td>
              <td className="p-3 font-semibold">{r.name}</td>
              <td className="p-3 text-gray-600">{r.department}</td>
              <td className="p-3 text-gray-700">{r.designation || '—'}</td>
              <td className="p-3 text-xs">{r.visa_company || '—'}</td>
              <td className="p-3 font-mono text-xs">{formatDate(r.start_date)}</td>
              <td className="p-3 font-mono text-xs">{r.end_date ? formatDate(r.end_date) : '—'}</td>
              <td className="p-3 text-right font-mono">{r.salary ? `AED ${formatNum(r.salary)}` : '—'}</td>
              <td className="p-3">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  r.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>{r.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (type === 'bank') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-sky-50 text-sky-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Code</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Department</th>
            <th className="text-left p-3">Bank Name</th>
            <th className="text-left p-3">Account No</th>
            <th className="text-left p-3">IBAN</th>
            <th className="text-left p-3">Payment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t hover:bg-sky-50/40" data-testid={`bank-row-${r.employee_code}`}>
              <td className="p-3 text-gray-500">{i + 1}</td>
              <td className="p-3 font-mono text-xs">{r.employee_code}</td>
              <td className="p-3 font-semibold">{r.name}</td>
              <td className="p-3 text-gray-600">{r.department}</td>
              <td className="p-3">{r.bank_name || '—'}</td>
              <td className="p-3 font-mono text-xs">{r.account_number || '—'}</td>
              <td className="p-3 font-mono text-xs">{r.iban || '—'}</td>
              <td className="p-3 text-xs text-gray-600">{r.payment_method || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (type === 'contact') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-emerald-50 text-emerald-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Code</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Department</th>
            <th className="text-left p-3">Personal Email</th>
            <th className="text-left p-3">Work Email</th>
            <th className="text-left p-3">Mobile</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t hover:bg-emerald-50/40" data-testid={`contact-row-${r.employee_code}`}>
              <td className="p-3 text-gray-500">{i + 1}</td>
              <td className="p-3 font-mono text-xs">{r.employee_code}</td>
              <td className="p-3 font-semibold">{r.name}</td>
              <td className="p-3 text-gray-600">{r.department}</td>
              <td className="p-3 text-xs">{r.personal_email || '—'}</td>
              <td className="p-3 text-xs">{r.work_email || '—'}</td>
              <td className="p-3 font-mono text-xs">{r.mobile_no || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (type === 'dependents') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-rose-50 text-rose-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Emp Code</th>
            <th className="text-left p-3">Employee</th>
            <th className="text-left p-3">Dependent</th>
            <th className="text-left p-3">Relation</th>
            <th className="text-left p-3">DOB</th>
            <th className="text-left p-3">Passport</th>
            <th className="text-center p-3">Insurance</th>
            <th className="text-right p-3">Cost (AED)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t hover:bg-rose-50/40">
              <td className="p-3 text-gray-500">{i + 1}</td>
              <td className="p-3 font-mono text-xs">{r.employee_code}</td>
              <td className="p-3 text-gray-700">{r.employee_name}</td>
              <td className="p-3 font-semibold">{r.dependent_name}</td>
              <td className="p-3"><span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700 text-xs">{r.relation}</span></td>
              <td className="p-3 text-xs">{formatDate(r.dob)}</td>
              <td className="p-3 font-mono text-xs">{r.passport_no || '—'}</td>
              <td className="p-3 text-center">
                <span className={r.insurance_sponsored === 'Yes' ? 'text-emerald-600 font-semibold' : 'text-gray-400'}>
                  {r.insurance_sponsored}
                </span>
              </td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.insurance_cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (type === 'staff-cost') {
    const totals = rows.reduce((acc, r) => ({
      gross: acc.gross + (r.gross || 0),
      total: acc.total + (r.total_monthly_cost || 0),
      dep: acc.dep + (r.dependent_cost_monthly || 0),
      totalDep: acc.totalDep + (r.total_with_dependents || r.total_monthly_cost || 0),
    }), { gross: 0, total: 0, dep: 0, totalDep: 0 });
    return (
      <table className="w-full text-sm">
        <thead className="bg-amber-50 text-amber-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Code</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Dept</th>
            <th className="text-right p-3">Basic</th>
            <th className="text-right p-3">Housing</th>
            <th className="text-right p-3">Trans</th>
            <th className="text-right p-3">Other</th>
            <th className="text-right p-3">Gross</th>
            <th className="text-right p-3">Visa/yr</th>
            <th className="text-right p-3">Ins/yr</th>
            <th className="text-right p-3 bg-amber-100">Total Monthly</th>
            {includeDependents && <th className="text-right p-3 bg-indigo-50 text-indigo-700">Dependents</th>}
            {includeDependents && <th className="text-right p-3 bg-indigo-50 text-indigo-700">Dep Cost/mo</th>}
            {includeDependents && <th className="text-right p-3 bg-indigo-100 text-indigo-800">Total incl. Dep</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t hover:bg-amber-50/40">
              <td className="p-3 text-gray-500">{i + 1}</td>
              <td className="p-3 font-mono text-xs">{r.employee_code}</td>
              <td className="p-3 font-semibold">{r.name}</td>
              <td className="p-3 text-xs text-gray-600">{r.department}</td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.basic)}</td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.housing)}</td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.transportation)}</td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.other)}</td>
              <td className="p-3 text-right font-mono text-xs font-semibold text-blue-700">{formatNum(r.gross)}</td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.visa_cost)}</td>
              <td className="p-3 text-right font-mono text-xs">{formatNum(r.insurance_cost)}</td>
              <td className="p-3 text-right font-mono text-xs font-bold text-amber-700 bg-amber-50">{formatNum(r.total_monthly_cost)}</td>
              {includeDependents && <td className="p-3 text-right font-mono text-xs text-indigo-600">{r.dependent_count || 0}</td>}
              {includeDependents && <td className="p-3 text-right font-mono text-xs text-indigo-600">{formatNum(r.dependent_cost_monthly || 0)}</td>}
              {includeDependents && <td className="p-3 text-right font-mono text-xs font-bold text-indigo-800 bg-indigo-50">{formatNum(r.total_with_dependents || r.total_monthly_cost)}</td>}
            </tr>
          ))}
          <tr className="bg-amber-100 font-bold">
            <td className="p-3" colSpan={8}>Total</td>
            <td className="p-3 text-right font-mono text-blue-800">{formatNum(totals.gross)}</td>
            <td colSpan={2}></td>
            <td className="p-3 text-right font-mono text-amber-800">{formatNum(totals.total)}</td>
            {includeDependents && <td></td>}
            {includeDependents && <td className="p-3 text-right font-mono text-indigo-800">{formatNum(totals.dep)}</td>}
            {includeDependents && <td className="p-3 text-right font-mono text-indigo-900 bg-indigo-100">{formatNum(totals.totalDep)}</td>}
          </tr>
        </tbody>
      </table>
    );
  }
  if (type === 'lop') {
    const allYears = (rows[0]?.years) || [];
    const isSingleYear = lopYearFilter !== 'all';
    const selYear = isSingleYear ? Number(lopYearFilter) : null;
    const visibleYears = isSingleYear
      ? allYears.filter(y => String(y) === String(lopYearFilter))
      : allYears;
    const showHistorical = !isSingleYear;
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthVal = (r, m) => r.by_month?.[`${selYear}-${m}`] || 0;
    const rowTotal = (r) => isSingleYear ? (r.by_year?.[selYear] || 0) : (r.total_lop_days || 0);
    const totals = rows.reduce((acc, r) => {
      if (isSingleYear) {
        for (let m = 1; m <= 12; m++) acc.months[m] = (acc.months[m] || 0) + monthVal(r, m);
      } else {
        visibleYears.forEach(y => { acc.years[y] = (acc.years[y] || 0) + (r.by_year?.[y] || 0); });
        acc.historical += r.historical || 0;
      }
      acc.total += rowTotal(r);
      return acc;
    }, { years: {}, months: {}, historical: 0, total: 0 });
    return (
      <>
      <table className="w-full text-sm">
        <thead className="bg-orange-50 text-orange-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Code</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Department</th>
            {isSingleYear
              ? MONTHS.map((mn, i) => (
                  <th key={i} className="text-right px-2 py-3">{mn}</th>
                ))
              : visibleYears.map(y => (
                  <th key={y} className="text-right p-3">{y}</th>
                ))}
            {showHistorical && (
              <th className="text-right p-3" title="Legacy/imported LOP without leave records">Pre-record</th>
            )}
            <th className="text-right p-3 bg-orange-100">{isSingleYear ? `Total ${selYear}` : 'Total LOP'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const rt = rowTotal(r);
            const hasLop = rt > 0;
            return (
              <tr key={i} className={`border-t ${hasLop ? 'bg-orange-50/50' : 'hover:bg-orange-50/40'}`} data-testid={`lop-row-${r.employee_code}`}>
                <td className="p-3 text-gray-500">{i + 1}</td>
                <td className="p-3 font-mono text-xs">{r.employee_code}</td>
                <td className="p-3 font-semibold">
                  <span className="inline-flex items-center gap-2">
                    {r.name}
                    {isSuperAdmin && onAddEntry && (
                      <button
                        type="button"
                        onClick={() => onAddEntry(r)}
                        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-orange-100 text-orange-600 hover:bg-orange-200 transition"
                        title="Add LOP entry for this employee"
                        data-testid={`add-lop-entry-${r.employee_code}`}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                </td>
                <td className="p-3 text-xs text-gray-600">{r.department}</td>
                {isSingleYear
                  ? MONTHS.map((mn, idx) => {
                      const m = idx + 1;
                      const days = monthVal(r, m);
                      return (
                        <td key={m} className={`px-2 py-3 text-right font-mono text-xs ${days > 0 ? 'text-orange-700 font-semibold' : 'text-gray-300'}`}>
                          {days > 0 ? (
                            <button
                              type="button"
                              onClick={() => openLopDetail(r, selYear, m)}
                              className="underline decoration-dotted underline-offset-2 hover:text-rose-700 hover:decoration-solid transition"
                              title={`View ${MONTHS[idx]} ${selYear} leave records`}
                              data-testid={`lop-cell-${r.employee_code}-${m}`}
                            >
                              {formatNum(days)}
                            </button>
                          ) : '—'}
                        </td>
                      );
                    })
                  : visibleYears.map(y => {
                      const days = r.by_year?.[y] || 0;
                      return (
                        <td key={y} className={`p-3 text-right font-mono text-xs ${days > 0 ? 'text-orange-700 font-semibold' : 'text-gray-400'}`}>
                          {days > 0 ? (
                            <button
                              type="button"
                              onClick={() => openLopDetail(r, y, null)}
                              className="underline decoration-dotted underline-offset-2 hover:text-rose-700 hover:decoration-solid transition"
                              title={`View ${y} leave records`}
                              data-testid={`lop-cell-${r.employee_code}-${y}`}
                            >
                              {formatNum(days)}
                            </button>
                          ) : '—'}
                        </td>
                      );
                    })}
                {showHistorical && (
                  <td className={`p-3 text-right font-mono text-xs ${r.historical > 0 ? 'text-amber-700 font-semibold italic' : 'text-gray-400'}`}>
                    <span className="inline-flex items-center gap-1">
                      <span>{r.historical > 0 ? formatNum(r.historical) : '—'}</span>
                      {isSuperAdmin && onEditPreRecord && (
                        <button
                          type="button"
                          onClick={() => onEditPreRecord(r)}
                          className="opacity-30 hover:opacity-100 transition"
                          title="Edit pre-record value"
                          data-testid={`edit-prerecord-${r.employee_code}`}
                        >
                          <Pencil className="h-3 w-3 text-gray-500 hover:text-orange-600" />
                        </button>
                      )}
                    </span>
                  </td>
                )}
                <td className={`p-3 text-right font-mono text-sm font-bold ${hasLop ? 'text-rose-700 bg-rose-50' : 'text-gray-400'}`}>
                  {hasLop ? formatNum(rt) : '—'}
                </td>
              </tr>
            );
          })}
          <tr className="bg-orange-100 font-bold">
            <td className="p-3" colSpan={4}>Total</td>
            {isSingleYear
              ? MONTHS.map((mn, idx) => (
                  <td key={idx} className="px-2 py-3 text-right font-mono text-orange-800">{formatNum(totals.months[idx + 1] || 0)}</td>
                ))
              : visibleYears.map(y => (
                  <td key={y} className="p-3 text-right font-mono text-orange-800">{formatNum(totals.years[y] || 0)}</td>
                ))}
            {showHistorical && (
              <td className="p-3 text-right font-mono text-amber-800">{formatNum(totals.historical)}</td>
            )}
            <td className="p-3 text-right font-mono text-rose-800">{formatNum(totals.total)}</td>
          </tr>
        </tbody>
      </table>
      <Dialog open={!!lopDetail} onOpenChange={(o) => { if (!o) setLopDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="lop-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertOctagon className="h-5 w-5 text-orange-600" />
              {lopDetail?.emp?.name} — LOP {lopDetail?.month ? `${MONTH_NAMES[lopDetail.month - 1]} ` : ''}{lopDetail?.year}
            </DialogTitle>
          </DialogHeader>
          {lopDetail?.loading ? (
            <div className="py-8 text-center text-gray-500">Loading leave records…</div>
          ) : (lopDetail?.leaves || []).length === 0 ? (
            <div className="py-8 text-center text-gray-500">No underlying leave records found for this period.</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-orange-50 text-orange-800 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2.5">Type</th>
                    <th className="text-left p-2.5">From</th>
                    <th className="text-left p-2.5">To</th>
                    <th className="text-right p-2.5">Days</th>
                    <th className="text-right p-2.5">LOP days</th>
                    <th className="text-left p-2.5">Reason</th>
                    {isSuperAdmin && <th className="text-center p-2.5">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {(lopDetail.leaves || []).map((lv, i) => (
                    <tr key={lv.id || i} className="border-t" data-testid={`lop-detail-row-${i}`}>
                      <td className="p-2.5 capitalize">{(lv.leave_type || '').replace(/_/g, ' ')}</td>
                      <td className="p-2.5 font-mono text-xs">{formatDate(lv.start_date)}</td>
                      <td className="p-2.5 font-mono text-xs">{formatDate(lv.end_date)}</td>
                      <td className="p-2.5 text-right font-mono">{formatNum(lv.days)}</td>
                      <td className="p-2.5 text-right font-mono font-semibold text-rose-700">{formatNum(lv.lop_days)}</td>
                      <td className="p-2.5 text-xs text-gray-600">{lv.reason || '—'}</td>
                      {isSuperAdmin && (
                        <td className="p-2.5 text-center">
                          {lv.source === 'manual_lop_entry' ? (
                            <button
                              type="button"
                              onClick={() => deleteLopEntry(lv)}
                              disabled={deletingId === lv.id}
                              title="Remove this manual LOP entry"
                              data-testid={`delete-lop-entry-${lv.id}`}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-rose-600 hover:bg-rose-50 disabled:opacity-40 transition"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <span className="text-[10px] text-gray-400" title="Manage in the Leaves module">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-orange-100 font-bold border-t">
                    <td className="p-2.5" colSpan={4}>Total LOP</td>
                    <td className="p-2.5 text-right font-mono text-rose-800">
                      {formatNum((lopDetail.leaves || []).reduce((s, l) => s + (l.lop_days || 0), 0))}
                    </td>
                    <td className="p-2.5"></td>
                    {isSuperAdmin && <td className="p-2.5"></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
    );
  }
  if (type === 'salary') {
    return <SalaryTable rows={rows} onSaveSalary={onSaveSalary} />;
  }
  if (type === 'iloe') {
    return <ILOETable rows={rows} onSaveIloe={onSaveIloe} />;
  }
  if (type === 'visa') {
    return (
      <table className="w-full text-sm">
        <thead className="bg-violet-50 text-violet-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Code</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Dept</th>
            <th className="text-left p-3">Visa No</th>
            <th className="text-left p-3">Sponsor</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3">Expiry</th>
            <th className="text-center p-3">Days Left</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const dleft = r.days_to_expiry;
            const danger = dleft !== null && dleft <= 30;
            const expired = dleft !== null && dleft < 0;
            return (
              <tr key={i} className={`border-t ${expired ? 'bg-rose-50' : danger ? 'bg-amber-50' : 'hover:bg-violet-50/40'}`}>
                <td className="p-3 text-gray-500">{i + 1}</td>
                <td className="p-3 font-mono text-xs">{r.employee_code}</td>
                <td className="p-3 font-semibold">{r.name}</td>
                <td className="p-3 text-xs text-gray-600">{r.department}</td>
                <td className="p-3 font-mono text-xs">{r.visa_number || '—'}</td>
                <td className="p-3">{r.visa_company || '—'}</td>
                <td className="p-3 text-xs">{r.visa_status || '—'}</td>
                <td className="p-3">{formatDate(r.visa_expiry)}</td>
                <td className="p-3 text-center">
                  {dleft === null ? <span className="text-gray-400">—</span>
                    : expired ? <span className="font-bold text-rose-600">Expired</span>
                    : <span className={`font-semibold ${danger ? 'text-amber-700' : 'text-emerald-600'}`}>{dleft} d</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  if (type === 'esc') {
    const fmt = (d) => {
      if (!d) return '—';
      try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
      catch { return d; }
    };
    return (
      <table className="w-full text-sm">
        <thead className="bg-red-50 text-red-800 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">S. No</th>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Occupation in (Visa)</th>
            <th className="text-left p-3">Job Title during (Event)</th>
            <th className="text-left p-3">Mobile Number</th>
            <th className="text-left p-3">Company Tel.</th>
            <th className="text-left p-3">EID Number</th>
            <th className="text-left p-3">Date of birth</th>
            <th className="text-left p-3">Religion</th>
            <th className="text-left p-3">Faith</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t hover:bg-red-50/40" data-testid={`esc-row-${r.employee_code}`}>
              <td className="p-3 text-gray-500">{i + 1}</td>
              <td className="p-3 font-semibold">{r.name}</td>
              <td className="p-3 text-gray-700">{r.occupation_visa || '—'}</td>
              <td className="p-3 text-gray-700">{r.job_title_event || '—'}</td>
              <td className="p-3 font-mono text-xs">{r.mobile || '—'}</td>
              <td className="p-3 font-mono text-xs">{r.company_tel || '—'}</td>
              <td className="p-3 font-mono text-xs">{r.eid || '—'}</td>
              <td className="p-3 font-mono text-xs">{fmt(r.dob)}</td>
              <td className="p-3 text-xs">{r.religion || '—'}</td>
              <td className="p-3 text-xs">{r.faith || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return null;
};

export default StaffDirectory;
